import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LaunchIcon from "@mui/icons-material/Launch";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";

import { getSignatureUi } from "../../../api/ui";

interface SignerItem {
  id?: string;
  name?: string;
  email?: string;
  document?: string;
  status?: string;
  signed?: boolean;
}

interface ProcessDetail {
  id?: string;
  title?: string;
  description?: string;
  status?: string;
  authorName?: string;
  authorEmail?: string;
  authorDocument?: string;
  createdAt?: string;
  hash?: string;
  fileUrl?: string;
  downloadUrl?: string;
  file?: {
    temporaryUrl?: string;
  };
  signers?: SignerItem[];
}

export function AssinaturasDetail(): React.ReactElement {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [process, setProcess] = useState<ProcessDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetail = async (): Promise<void> => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await getSignatureUi(id) as ProcessDetail;
        setProcess(data);
      } catch (err: unknown) {
        const errorObj = err as { response?: { data?: { message?: string } } };
        const msg = errorObj.response?.data?.message;
        setError(typeof msg === "string" ? msg : t("assinaturas.detail.loadError"));
      } finally {
        setLoading(false);
      }
    };

    void fetchDetail();
  }, [id, t]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !process) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t("assinaturas.detail.notFound")}
        </Alert>
        <Button onClick={() => { navigate("/assinaturas"); }} startIcon={<ArrowBackIcon />}>
          {t("assinaturas.detail.backToList")}
        </Button>
      </Box>
    );
  }

  const getStatusChip = (status?: string): React.ReactElement => {
    const upper = (status || "").toUpperCase();
    if (
      upper === "COMPLETED" ||
      upper === "FINISHED" ||
      upper === "CONCLUIDO" ||
      upper === "CONCLUÍDO" ||
      upper === "SIGNED" ||
      upper === "ASSINADO"
    ) {
      return (
        <Chip
          label={t("assinaturas.list.statusCompleted") || "CONCLUÍDO"}
          sx={{
            bgcolor: "#22c55e",
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: "0.75rem",
            height: 28,
            px: 1,
            borderRadius: "14px",
          }}
        />
      );
    }
    if (upper === "PENDING" || upper === "PENDENTE") {
      return (
        <Chip
          label={t("assinaturas.list.statusPending") || "PENDENTE"}
          sx={{
            bgcolor: "#f97316",
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: "0.75rem",
            height: 28,
            px: 1,
            borderRadius: "14px",
          }}
        />
      );
    }
    if (upper === "EXPIRED" || upper === "EXPIRADO") {
      return (
        <Chip
          label={t("assinaturas.list.statusExpired") || "EXPIRADO"}
          sx={{
            bgcolor: "#dc2626",
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: "0.75rem",
            height: 28,
            px: 1,
            borderRadius: "14px",
          }}
        />
      );
    }
    return (
      <Chip
        label={status || "N/A"}
        sx={{
          bgcolor: "#64748b",
          color: "#ffffff",
          fontWeight: "bold",
          fontSize: "0.75rem",
          height: 28,
          px: 1,
          borderRadius: "14px",
        }}
      />
    );
  };

  const fileUrl = process.fileUrl || process.downloadUrl || process.file?.temporaryUrl;
  const hashOrId = process.hash || process.id || "";

  const handleViewDocument = (): void => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    } else if (process.id) {
      window.open(`/api/v1/signatures/${process.id}/download-zip`, "_blank");
    }
  };

  const handleOpenValidatePage = (): void => {
    window.open(`/validate/${hashOrId}`, "_blank");
  };

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      {/* Top Header Row */}
      <Stack
        alignItems={{ xs: "flex-start", sm: "center" }}
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton edge="start" onClick={() => { navigate("/assinaturas"); }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography color="#1e293b" component="h1" fontWeight="bold" variant="h5">
            {t("assinaturas.detail.title") || "Detalhes da Assinatura"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            onClick={handleViewDocument}
            startIcon={<VisibilityIcon />}
            sx={{
              borderColor: "#2F16C9",
              color: "#2F16C9",
              fontWeight: "bold",
              textTransform: "none",
              borderRadius: 1.5,
              px: 2,
              py: 0.8,
              "&:hover": {
                borderColor: "#1e0ea3",
                backgroundColor: "rgba(47, 22, 201, 0.04)",
              },
            }}
            variant="outlined"
          >
            {t("assinaturas.detail.viewDocument") || "Visualizar Documento"}
          </Button>

          <Button
            onClick={handleOpenValidatePage}
            startIcon={<LaunchIcon />}
            sx={{
              borderColor: "#2F16C9",
              color: "#2F16C9",
              fontWeight: "bold",
              textTransform: "none",
              borderRadius: 1.5,
              px: 2,
              py: 0.8,
              "&:hover": {
                borderColor: "#1e0ea3",
                backgroundColor: "rgba(47, 22, 201, 0.04)",
              },
            }}
            variant="outlined"
          >
            {t("assinaturas.detail.validatePage") || "Página de Validação"}
          </Button>

          {getStatusChip(process.status)}
        </Box>
      </Stack>

      {/* Process Info Section Card */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        <Typography color="#1e293b" fontWeight="bold" gutterBottom variant="h6">
          {process.title || t("assinaturas.detail.noTitle")}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {process.description || process.title || "-"}
        </Typography>

        <Divider sx={{ my: 2.5 }} />

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography color="text.secondary" display="block" gutterBottom variant="caption">
              {t("assinaturas.detail.author")}
            </Typography>
            <Typography color="#1e293b" fontWeight="bold" sx={{ wordBreak: "break-all" }} variant="body2">
              {process.authorName || "-"} {process.authorEmail ? `(${process.authorEmail})` : ""}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography color="text.secondary" display="block" gutterBottom variant="caption">
              {t("assinaturas.detail.authorDocument")}
            </Typography>
            <Typography color="#1e293b" fontWeight="bold" variant="body2">
              {process.authorDocument || "-"}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography color="text.secondary" display="block" gutterBottom variant="caption">
              {t("assinaturas.detail.createdAt")}
            </Typography>
            <Typography color="#1e293b" fontWeight="bold" variant="body2">
              {process.createdAt
                ? dayjs(process.createdAt).format("DD/MM/YYYY, HH:mm:ss")
                : "-"}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography color="text.secondary" display="block" gutterBottom variant="caption">
              {t("assinaturas.detail.processId")}
            </Typography>
            <Typography color="#1e293b" fontWeight="bold" sx={{ wordBreak: "break-all" }} variant="body2">
              {process.id || process.hash || "-"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Signers Section Card */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        <Typography color="#1e293b" fontWeight="bold" sx={{ mb: 2.5 }} variant="h6">
          {t("assinaturas.detail.signersTitle", { count: process.signers?.length || 0 })}
        </Typography>

        <Stack spacing={2}>
          {process.signers && process.signers.length > 0 ? (
            process.signers.map((signer: SignerItem, index: number) => {
              const isSignerSigned =
                signer.signed || signer.status === "SIGNED" || signer.status === "CONCLUIDO";
              const isSignerPending =
                signer.status === "PENDING" ||
                signer.status === "PENDENTE" ||
                (!isSignerSigned && !signer.status);

              return (
                <Box
                  key={signer.id || index}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography color="#1e293b" fontWeight="bold" variant="subtitle1">
                      {signer.name || t("assinaturas.detail.signerDefaultName")}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      {signer.email || "-"}
                      {signer.document ? ` | CPF: ${signer.document}` : ""}
                    </Typography>
                  </Box>

                  <Box>
                    {Boolean(isSignerSigned) && (
                      <Chip
                        icon={<TaskAltIcon style={{ color: "#15803d", fontSize: 16 }} />}
                        label={t("assinaturas.detail.statusCompleted")}
                        sx={{
                          bgcolor: "#dcfce7",
                          color: "#15803d",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          height: 28,
                          borderRadius: "14px",
                        }}
                      />
                    )}
                    {Boolean(!isSignerSigned && isSignerPending) && (
                      <Chip
                        label={t("assinaturas.detail.statusPending")}
                        sx={{
                          bgcolor: "#ff9800",
                          color: "#ffffff",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          height: 28,
                          borderRadius: "14px",
                          px: 0.5,
                        }}
                      />
                    )}
                    {Boolean(!isSignerSigned && !isSignerPending) && (
                      <Chip
                        label={signer.status || t("assinaturas.detail.statusPending")}
                        sx={{
                          bgcolor: "#ff9800",
                          color: "#ffffff",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          height: 28,
                          borderRadius: "14px",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography color="text.secondary" variant="body2">
              {t("assinaturas.detail.noSigners")}
            </Typography>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default AssinaturasDetail;
